import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { InputAdornment } from '@mui/material';
import { ThemeProvider } from "@mui/material/styles";
import { InputTheme } from "../../Pickers/InputTheme";
import flags from 'react-phone-number-input/flags';
import './InternationalPhoneInputDisplayOnly.scss';

/**
 * Read-only phone display component with country flag
 * Shows local phone format (e.g., '0504499222') with the country flag
 * Uses Material UI TextField for consistent styling with other form fields
 *
 * @param {object} props - Component props
 * @param {string} props.value - Phone number to display (local format)
 * @param {string} props.countryCode - Country code for flag (e.g., 'IL', 'US')
 * @param {string} [props.label] - Input label text
 * @param {string} [props.dataTestId] - Test ID for testing
 * @param {JSX.Element} [props.endAdornment] - Optional end adornment (e.g., edit icon)
 * @returns {JSX.Element} InternationalPhoneInputDisplayOnly component
 */
export const InternationalPhoneInputDisplayOnly = ({
    value,
    countryCode,
    label,
    dataTestId,
    endAdornment
}) => {
    const [currentCountry, setCurrentCountry] = useState(countryCode);

    // Update country when countryCode prop changes
    useEffect(() => {
        if (countryCode) {
            setCurrentCountry(countryCode);
        }
    }, [countryCode]);

    const FlagComponent = currentCountry ? flags[currentCountry] : null;

    return (
        <ThemeProvider theme={InputTheme}>
            <TextField
                className="international-phone-input-display-only form-input"
                label={label}
                value={value || ''}
                disabled
                InputLabelProps={{ shrink: true }}
                InputProps={{
                    startAdornment: FlagComponent ? (
                        <InputAdornment position="start">
                            <span className="international-phone-input-display-only-flag">
                                <FlagComponent title={currentCountry} />
                            </span>
                        </InputAdornment>
                    ) : null,
                    endAdornment: endAdornment ? (
                        <InputAdornment position="end">
                            {endAdornment}
                        </InputAdornment>
                    ) : null
                }}
                inputProps={{
                    'data-testid': dataTestId
                }}
            />
        </ThemeProvider>
    );
};
